import { useEffect } from "react";

const defaultMeta = {
  title: "UP NEXT CALENDAR - Malaysia Events & Festivals 2026 | Visit Malaysia Year",
  description: "Discover 47+ major events in Malaysia 2026! Concerts (K-pop, Rock), Sports (MotoGP, Marathon), Cultural Festivals, Tech Expos & more. Your complete guide to Visit Malaysia Year 2026.",
  keywords: "Malaysia 2026, Visit Malaysia Year, Malaysia events, Malaysia festivals",
  image: `${window.location.origin}/og-image.png`,
  url: window.location.origin
};

export default function SEO({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = "website",
  event = null 
}) {
  useEffect(() => {
    // Update title
    document.title = title || defaultMeta.title;
    
    // Update meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };
    
    const desc = description || defaultMeta.description;
    const img = image || defaultMeta.image;
    const pageUrl = url || window.location.href;
    const pageTitle = title || defaultMeta.title;
    
    // Basic meta
    updateMeta("description", desc);
    updateMeta("keywords", keywords || defaultMeta.keywords);
    
    // Open Graph - Essential for WhatsApp, Facebook, Telegram
    updateMeta("og:title", pageTitle, true);
    updateMeta("og:description", desc, true);
    updateMeta("og:image", img, true);
    updateMeta("og:image:width", "1200", true);
    updateMeta("og:image:height", "630", true);
    updateMeta("og:url", pageUrl, true);
    updateMeta("og:type", event ? "article" : type, true);
    updateMeta("og:site_name", "UP NEXT CALENDAR Malaysia 2026", true);
    updateMeta("og:locale", "en_MY", true);
    
    // Twitter Card - For Twitter/X
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", pageTitle);
    updateMeta("twitter:description", desc);
    updateMeta("twitter:image", img);
    updateMeta("twitter:site", "@VisitMalaysia");
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", pageUrl);
    
    // Event structured data (JSON-LD) for rich search results
    if (event) {
      let script = document.getElementById("event-schema");
      if (!script) {
        script = document.createElement("script");
        script.id = "event-schema";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      
      const eventSchema = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": event.title,
        "description": event.description,
        "startDate": event.date,
        "endDate": event.end_date || event.date,
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": event.location,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "MY",
            "addressLocality": event.location
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": "UP NEXT CALENDAR Malaysia 2026",
          "url": window.location.origin
        },
        "image": event.image_url || img
      };
      
      // Add price info if available
      if (event.price && event.price !== "Free") {
        eventSchema.offers = {
          "@type": "Offer",
          "price": event.price,
          "priceCurrency": "MYR",
          "availability": "https://schema.org/InStock",
          "url": pageUrl
        };
      }
      
      script.textContent = JSON.stringify(eventSchema);
    } else {
      // Remove event schema if not on event page
      const existingScript = document.getElementById("event-schema");
      if (existingScript) {
        existingScript.remove();
      }
    }
    
  }, [title, description, keywords, image, url, type, event]);
  
  return null;
}

// Page-specific SEO configs
export const pageSEO = {
  home: {
    title: "UP NEXT CALENDAR - Malaysia Events & Festivals 2026 | Visit Malaysia Year",
    description: "Discover 47+ major events in Malaysia 2026! K-pop concerts, MotoGP, cultural festivals, tech expos & more. Your complete guide to Visit Malaysia Year 2026.",
    keywords: "Malaysia 2026, Visit Malaysia Year, Malaysia events, K-pop Malaysia, MotoGP Sepang, Hari Raya, Chinese New Year Malaysia, Good Vibes Festival",
    image: "/og-image.png"
  },
  events: {
    title: "All Events Malaysia 2026 - Concerts, Sports, Festivals | UP NEXT CALENDAR",
    description: "Browse 47+ events happening in Malaysia 2026. Filter by category: Music & Concerts, Sports, Cultural Festivals, Tech Expos, Lifestyle. Plan your Visit Malaysia Year!",
    keywords: "Malaysia events 2026, Malaysia concerts, Malaysia festivals, Malaysia sports events, what to do in Malaysia 2026",
    image: "/og-image.png"
  },
  calendar: {
    title: "Malaysia Events Calendar 2026 - Monthly View | UP NEXT CALENDAR",
    description: "View all Malaysia 2026 events in calendar format. See what's happening each month - from Thaipusam in February to MotoGP in October. Plan ahead!",
    keywords: "Malaysia calendar 2026, Malaysia events calendar, Malaysia public holidays 2026, Visit Malaysia calendar",
    image: "/og-image.png"
  },
  about: {
    title: "About UP NEXT CALENDAR - Your Guide to Visit Malaysia 2026",
    description: "UP NEXT CALENDAR is your free guide to events and festivals during Visit Malaysia Year 2026. Discover concerts, sports, cultural celebrations and more across Malaysia.",
    keywords: "about UP NEXT CALENDAR, Visit Malaysia 2026, Malaysia tourism, Malaysia events guide",
    image: "/og-image.png"
  },
  bookmarks: {
    title: "My Saved Events - UP NEXT CALENDAR Malaysia 2026",
    description: "View your saved events for Malaysia 2026. Keep track of concerts, festivals, and activities you want to attend.",
    keywords: "saved events Malaysia, my events, bookmarked events",
    image: "/og-image.png"
  },
  music: {
    title: "Music & Concerts in Malaysia 2026 - K-pop, Rock, EDM | UP NEXT CALENDAR",
    description: "All music events in Malaysia 2026! Super Junior, Day6, IVE, My Chemical Romance, Good Vibes Festival, Rainforest World Music Festival & more.",
    keywords: "K-pop concert Malaysia 2026, Super Junior Malaysia, rock concert KL, EDM festival Malaysia, Good Vibes Festival 2026",
    image: "/og-image.png"
  },
  sports: {
    title: "Sports Events Malaysia 2026 - MotoGP, Marathon, Cycling | UP NEXT CALENDAR",
    description: "Sports events in Malaysia 2026: MotoGP Sepang, KL Marathon, Le Tour de Langkawi, Ironman 70.3, Malaysia Open Badminton & more.",
    keywords: "MotoGP Sepang 2026, KL Marathon 2026, Malaysia sports events, Le Tour de Langkawi, Ironman Malaysia",
    image: "/og-image.png"
  },
  cultural: {
    title: "Cultural & Religious Festivals Malaysia 2026 | UP NEXT CALENDAR",
    description: "Malaysia's cultural festivals 2026: Thaipusam, Chinese New Year, Hari Raya, Kaamatan, Gawai, Deepavali, George Town Festival & more.",
    keywords: "Thaipusam 2026, Hari Raya 2026, Chinese New Year Malaysia, Deepavali Malaysia, Malaysia cultural festivals",
    image: "/og-image.png"
  },
  tech: {
    title: "Tech & Business Expos Malaysia 2026 - MATTA, MTE | UP NEXT CALENDAR",
    description: "Technology and business events in Malaysia 2026: MATTA Fair, Malaysia Technology Expo, MIHAS, KL Book Fair & more.",
    keywords: "MATTA Fair 2026, Malaysia Technology Expo, MIHAS 2026, business events Malaysia",
    image: "/og-image.png"
  },
  lifestyle: {
    title: "Lifestyle & Pop Culture Events Malaysia 2026 | UP NEXT CALENDAR",
    description: "Lifestyle events in Malaysia 2026: Comic Fiesta, AniManGaki, Bon Odori, Art of Speed, Big Bad Wolf Book Sale & more.",
    keywords: "Comic Fiesta 2026, anime convention Malaysia, cosplay Malaysia, book fair Malaysia",
    image: "/og-image.png"
  }
};
